/* The offline pack.
 *
 * Ledger documents how to recover from a disaster, and Ledger lives in
 * SharePoint. If the tenant is down — the exact event the Backup & DR register
 * exists for — the document telling you what to do is inside the thing that is
 * broken. That is not a plan.
 *
 * The PWA caches, but only pages somebody has already visited, only on that
 * device, and only until the browser decides to reclaim the space. Relying on it
 * is relying on a cache to be a backup.
 *
 * So this is one page you can print, or save as a PDF and keep somewhere that
 * is not Microsoft: a USB stick in the Hull safe, a laptop that goes home, the
 * folder the DR runbook already lives in. Print it quarterly and after anything
 * significant changes.
 *
 * What is in it is decided by one question: at 3am, with nothing working, what
 * would you need to have in your hand? Circuits and who to ring. Where the comms
 * rooms are and who can let you in. What the backups cover, what they promise
 * and where the copies live. The recovery plans. Which credentials exist and
 * which vault holds them — never a secret, §16, and never one printed on paper
 * left in a drawer.
 *
 * People are deliberately absent. A hundred and eighty rows of staff data is not
 * what a recovery needs, and printing it makes a document that has to be kept
 * securely for a reason that has nothing to do with IT.
 */

import { useQuery } from '@tanstack/react-query';
import { useDepots } from '../../hooks/useDepots';
import { fmtDate } from '../../lib/format';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Button, Panel } from '../ui';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/** Long enough to be worth a warning. A pack printed a year ago describes a
    network that has probably moved on, and reading it during an incident is
    worse than having none — you would act on it. */
const STALE_DAYS = 120;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={s.packSection}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Rows({ rows }: { rows: [string, React.ReactNode][] }) {
  const filled = rows.filter(([, v]) => v !== '' && v !== undefined && v !== null);
  if (filled.length === 0) return <p className={s.cellSub}>Nothing recorded.</p>;
  return (
    <table className={s.packTable}>
      <tbody>
        {filled.map(([k, v]) => (
          <tr key={k}>
            <th>{k}</th>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PackScreen() {
  const svc = useServices();
  const depots = useDepots();

  const locations = useQuery({ queryKey: qk.locations, queryFn: () => svc.locations.all() });
  const networks = useQuery({ queryKey: qk.networks, queryFn: () => svc.networks.all() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });
  const applications = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });
  const backups = useQuery({ queryKey: qk.backups, queryFn: () => svc.backups.jobs() });
  const plans = useQuery({ queryKey: qk.plans, queryFn: () => svc.backups.plans() });
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const domains = useQuery({ queryKey: qk.domains, queryFn: () => svc.domains.list() });

  const loading = [locations, networks, suppliers, applications, backups, plans, credentials, devices, domains]
    .some((q) => q.isLoading);

  const printedOn = new Date();

  /* Infrastructure only. Sixty-nine laptops is a stock list, not a recovery
     document — the firewall, the switches and the server are what somebody
     standing in the comms room needs to identify. */
  const infrastructure = (devices.data ?? []).filter(
    (d) => !['Laptop', 'Desktop', 'Mobile', 'Tablet'].includes(d.type),
  );

  const critical = (applications.data ?? []).filter((a) => a.crit === 1);

  return (
    <div className={s.pack}>
      <div className={`${s.head} ${s.noPrint}`}>
        <div className={s.grow}>
          <h1>Offline pack</h1>
          <p>
            Everything a recovery needs, on one page. Print it or save it as a PDF and keep a
            copy outside Microsoft 365.
          </p>
        </div>
        <Button primary icon="ti-printer" onClick={() => window.print()} disabled={loading}>
          Print or save as PDF
        </Button>
      </div>

      <div className={`${s.ownedNote} ${s.noPrint}`}>
        <i className="ti ti-info-circle" aria-hidden />
        <span>
          A snapshot, not a live view. Print it again after anything significant changes, and
          quarterly regardless — a pack more than {STALE_DAYS} days old describes a network that
          has probably moved on, and acting on it during an incident is worse than having none.
          Nothing secret is printed: credentials appear by name and vault only.
        </span>
      </div>

      {loading ? (
        <Panel flush>
 <p className={`${s.cellSub} ${s.padBox}`} >
            Gathering every register…
          </p>
        </Panel>
      ) : (
        <article className={s.packDoc}>
          <header className={s.packHead}>
            <h1>{PRODUCT.fullName} — recovery pack</h1>
            <p>
              Printed {fmtDate(printedOn.toISOString())} · a snapshot of the register
              · no passwords are included
            </p>
          </header>

          <Section title="Sites">
            {depots.map((d) => {
              const site = locations.data?.[d.id];
              return (
                <div key={d.id} className={s.packBlock}>
                  <h3>
                    {d.name} {site?.role ? `— ${site.role}` : ''}
                  </h3>
                  <Rows
                    rows={[
                      ['Address', site?.addr],
                      ['Postcode', site?.postcode],
                      ['Main number', site?.phone],
                      ['Opening hours', site?.hours],
                      ['Comms room', site?.comms],
                      ['Access', site?.access],
                    ]}
                  />
                </div>
              );
            })}
          </Section>

          <Section title="Circuits">
            {depots.map((d) => {
              const net = networks.data?.[d.id];
              if (!net) return null;
              return (
                <div key={d.id} className={s.packBlock}>
                  <h3>{d.name}</h3>
                  <Rows
                    rows={[
                      ['Provider', net.provider],
                      ['Circuit reference', net.wan],
                      ['Type and speed', [net.wanType, net.speed].filter(Boolean).join(' · ')],
                      ['Account number', net.wanAcct],
                      ['Support line', net.wanSupport],
                      ['Public IP block', net.block],
                      ['DNS servers', net.dns],
                      ['Failover', net.failover],
                    ]}
                  />

                  {net.subnets.length > 0 && (
                    <table className={s.packTable}>
                      <thead>
                        <tr>
                          <th>VLAN</th>
                          <th>Name</th>
                          <th>Subnet</th>
                          <th>Gateway</th>
                          <th>DHCP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {net.subnets.map((x) => (
                          <tr key={x.cidr}>
                            <td>{x.vlan}</td>
                            <td>{x.name}</td>
                            <td>{x.cidr}</td>
                            <td>{x.gw}</td>
                            <td>{x.dhcp ? x.dhcpRange || 'Yes' : 'Static only'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {net.vpn.length > 0 && (
                    <Rows
                      rows={net.vpn.map((v) => [v.name, `${v.type} → ${v.dest}${v.purpose ? ` · ${v.purpose}` : ''}`])}
                    />
                  )}
                </div>
              );
            })}
          </Section>

          <Section title="Backups">
            {(backups.data ?? []).map((b) => (
              <div key={b.id} className={s.packBlock}>
                <h3>{b.name}</h3>
                <Rows
                  rows={[
                    ['Covers', b.what],
                    ['Product', b.product],
                    ['Method', b.method],
                    ['Schedule', b.sched],
                    ['Retention', b.ret],
                    ['Destination', b.dest],
                    ['RPO / RTO', [b.rpo, b.rto].filter(Boolean).join('  /  ')],
                    ['Offsite', b.offsite ? 'Yes' : 'No'],
                    ['Immutable', b.immutable ? 'Yes' : 'No'],
                    ['Last proven restore', b.tested && b.tested !== '—' ? fmtDate(b.tested) : 'Never'],
                  ]}
                />
              </div>
            ))}
          </Section>

          <Section title="Recovery plans">
            {(plans.data ?? []).map((p) => (
              <div key={p.id} className={s.packBlock}>
                <h3>{p.name}</h3>
                <Rows rows={[['Trigger', p.trigger], ['Target', p.rto], ['Last exercised', p.last]]} />
                <ol className={s.packSteps}>
                  {p.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            ))}
          </Section>

          <Section title="Who to ring">
            <table className={s.packTable}>
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>For</th>
                  <th>Account</th>
                  <th>Support line</th>
                </tr>
              </thead>
              <tbody>
                {(suppliers.data ?? []).map((x) => (
                  <tr key={x.id}>
                    <td>{x.name}</td>
                    <td>{x.service}</td>
                    <td>{x.acct}</td>
                    <td>{x.support}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Critical applications">
            <table className={s.packTable}>
              <thead>
                <tr>
                  <th>Application</th>
                  <th>What it does</th>
                  <th>Hosted</th>
                  <th>Supplier</th>
                </tr>
              </thead>
              <tbody>
                {critical.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td>{a.purpose}</td>
                    <td>{a.host}</td>
                    <td>{a.supplier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Infrastructure">
            <table className={s.packTable}>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Type</th>
                  <th>Make and model</th>
                  <th>Site</th>
                  <th>Address</th>
                  <th>Out-of-band</th>
                  <th>Login held in</th>
                </tr>
              </thead>
              <tbody>
                {infrastructure.map((d) => (
                  <tr key={d.id}>
                    <td>{d.tag}</td>
                    <td>{d.type}</td>
                    <td>
                      {d.make} {d.model}
                    </td>
                    <td>{[d.loc, d.placement].filter(Boolean).join(' · ')}</td>
                    <td>{d.ip}</td>
                    {/* The address that answers when the machine does not, which
                        is the whole reason to print any of this. */}
                    <td>{d.mgmtAddress ? `${d.mgmtAddress}${d.mgmtType ? ` (${d.mgmtType})` : ''}` : ''}</td>
                    <td>{d.mgmtCredential ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Domains">
            <table className={s.packTable}>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Registrar</th>
                  <th>Registration ends</th>
                  <th>Certificate ends</th>
                  <th>DNS</th>
                </tr>
              </thead>
              <tbody>
                {(domains.data ?? []).map((d) => (
                  <tr key={d.id}>
                    <td>{d.name}</td>
                    <td>{d.reg}</td>
                    <td>{d.regEnds ? fmtDate(d.regEnds) : ''}</td>
                    <td>{d.sslEnds ? fmtDate(d.sslEnds) : ''}</td>
                    <td>{d.dns}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Credentials — the index only">
            <p className={s.cellSub}>
              Which credentials exist and which vault holds them. No secret has ever been stored
              in {PRODUCT.name} and none is printed here — you still need access to the vault, which is
              the point.
            </p>
            <table className={s.packTable}>
              <thead>
                <tr>
                  <th>Credential</th>
                  <th>System</th>
                  <th>Held in</th>
                </tr>
              </thead>
              <tbody>
                {(credentials.data ?? []).map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.system}</td>
                    <td>{c.vault}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <footer className={s.packFoot}>
            {PRODUCT.fullName} · printed {fmtDate(printedOn.toISOString())} · reprint after
            any significant change, and quarterly regardless
          </footer>
        </article>
      )}
    </div>
  );
}
